/**
 * My Marketing — per-LO marketing hub for Origin CRM.
 *
 * Four tabs:
 *   Overview    — KPIs + weekly engagement chart + active campaign toggles
 *   Newsletters — create/manage recurring realtor newsletters
 *   Templates   — company newsletter templates ("Use template" → new draft)
 *   Activity    — unified log of every marketing send with engagement
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  HStack,
  VStack,
  Icon,
  Button,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  FormControl,
  FormLabel,
  Select as ChakraSelect,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiSend, FiPlus, FiEye } from 'react-icons/fi';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { groupCRMUsersByDTC } from '../../../utils/crmUserSorting';
import { useAuth } from '../../../contexts/AuthContext';
import { useTourActions } from '../../../components/FeatureTour/FeatureTourProvider';
import useNewsletters from '../../../hooks/useNewsletters';
import { createNewsletter, DEFAULT_AUDIENCE_CONTACT_TYPES } from '../../../api/supabase/newsletters';
import { DEFAULT_TIMEZONE } from '../../../utils/newsletterSchedule';
import OverviewTab from './components/OverviewTab';
import NewslettersTab from './components/NewslettersTab';
import TemplatesTab from './components/TemplatesTab';
import ActivityTab from './components/ActivityTab';

export const DEFAULT_SCHEDULE_CONFIG = {
  mode: 'recurring',
  frequency: 'monthly',
  dayOfMonth: 1,
  weekday: 2,
  hour: 9,
  minute: 0,
  timezone: DEFAULT_TIMEZONE,
};

export default function MyMarketingPage() {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const history = useHistory();
  const toast = useToast();
  const uid = currentUser?.uid;
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const iconBg = useColorModeValue('blue.50', 'blue.900');
  const bannerBg = useColorModeValue('blue.50', 'blue.900');
  const bannerBorder = useColorModeValue('blue.200', 'blue.700');
  const bannerText = useColorModeValue('blue.800', 'blue.100');
  const pickerLabelColor = useColorModeValue('gray.600', 'gray.400');

  const [tabIndex, setTabIndex] = useState(0);
  const [creating, setCreating] = useState(false);

  // Admin "view as" — mirrors SettingsPage: admins pick any CRM user and see
  // (and act on) this page exactly as that user, scoped by effectiveUserId.
  const [crmUsers, setCrmUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const effectiveUserId = (isCRMAdmin && selectedUserId) || uid;
  const isViewingOtherUser = Boolean(isCRMAdmin && selectedUserId && selectedUserId !== uid);
  const viewedUser = isViewingOtherUser ? crmUsers.find((u) => u.uid === selectedUserId) : null;

  useEffect(() => {
    if (!isCRMAdmin) return undefined;
    const q = query(collection(db, 'users'), where('CRMUser', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const users = [];
      snapshot.forEach((docSnap) => {
        const userData = docSnap.data();
        users.push({
          uid: docSnap.id,
          name: userData.Name || userData.name || userData.displayName || userData.email,
          email: userData.Email || userData.email,
          ...userData,
        });
      });
      setCrmUsers(users);
    });
    return () => unsubscribe();
  }, [isCRMAdmin]);

  const newslettersHook = useNewsletters(effectiveUserId);

  const handleCreateNewsletter = useCallback(async (prefill = {}) => {
    if (!effectiveUserId || creating) return;
    setCreating(true);
    try {
      const draft = await createNewsletter({
        ownerId: effectiveUserId,
        name: prefill.name || 'Untitled Newsletter',
        description: prefill.description || null,
        subject: prefill.subject || null,
        bodyHtml: prefill.bodyHtml || null,
        sourceTemplateId: prefill.sourceTemplateId || null,
        // 'template' = stay synced to a company template (latest version sends)
        contentSource: prefill.contentSource || 'custom',
        linkedTemplateId: prefill.linkedTemplateId || null,
        audienceConfig: prefill.audienceConfig || {
          contactTypes: [...DEFAULT_AUDIENCE_CONTACT_TYPES],
          tagsAny: [],
        },
        scheduleConfig: prefill.scheduleConfig || { ...DEFAULT_SCHEDULE_CONFIG },
        status: 'draft',
      });
      history.push(`/crm/my-marketing/newsletters/${draft.id}`);
    } catch (err) {
      console.error('[MyMarketing] Create newsletter failed:', err);
      toast({
        title: 'Could not create newsletter',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  }, [effectiveUserId, creating, history, toast]);

  // FeatureTour: named actions the My Marketing tours can invoke — click the
  // tagged tab header (never index math) so isLazy panels mount naturally.
  // openNewsletterForTour backs the Newsletter Editor entry in this page's
  // tutorials menu (registry menuAlso): open the newest newsletter, or create
  // a fresh draft if they don't have one yet — the tour starts on arrival.
  const latestNewsletterId = newslettersHook.newsletters?.[0]?.id || null;
  useTourActions(useMemo(() => {
    const clickTab = (id) => document.querySelector(`[data-tour="${id}"]`)?.click();
    return {
      showOverviewTab: () => clickTab('mm-tab-overview'),
      showNewslettersTab: () => clickTab('mm-tab-newsletters'),
      showTemplatesTab: () => clickTab('mm-tab-templates'),
      showActivityTab: () => clickTab('mm-tab-activity'),
      openNewsletterForTour: () => {
        if (latestNewsletterId) {
          history.push(`/crm/my-marketing/newsletters/${latestNewsletterId}`);
        } else {
          return handleCreateNewsletter();
        }
        return undefined;
      },
    };
  }, [latestNewsletterId, history, handleCreateNewsletter]));

  return (
    <Box minH="100vh" bg={pageBg} pb={12}>
      <Container maxW="1400px" pt={6}>
        {/* Header */}
        <Box
          data-tour="mm-header"
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          shadow="sm"
          px={{ base: 4, md: 6 }}
          py={5}
          mb={5}
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            gap={3}
          >
            <HStack spacing={4}>
              <Box bg={iconBg} p={3} borderRadius="lg">
                <Icon as={FiSend} boxSize={6} color="blue.500" />
              </Box>
              <Box>
                <Heading size="md">
                  {isViewingOtherUser ? `${viewedUser?.name || 'User'}'s Marketing` : 'My Marketing'}
                </Heading>
                <Text fontSize="sm" color={subtitleColor}>
                  Newsletters, campaigns, and every marketing send — all in one place
                </Text>
              </Box>
            </HStack>
            <HStack spacing={3} align="flex-end">
              {/* Admin "view as" picker — same pattern as CRM Settings */}
              {isCRMAdmin && (
                <VStack align="flex-end" spacing={1}>
                  <FormControl maxW="260px" data-tour="mm-view-as">
                    <FormLabel
                      fontSize="xs"
                      mb={1}
                      textTransform="uppercase"
                      fontWeight="semibold"
                      color={pickerLabelColor}
                    >
                      View Marketing For
                    </FormLabel>
                    <ChakraSelect
                      size="sm"
                      placeholder="My Marketing"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      {(() => {
                        const { dtcUsers, otherUsers } = groupCRMUsersByDTC(crmUsers);
                        return (
                          <>
                            {dtcUsers.length > 0 && (
                              <optgroup label="DTC Loan Officers">
                                {dtcUsers.map((user) => (
                                  <option key={user.uid} value={user.uid}>{user.name}</option>
                                ))}
                              </optgroup>
                            )}
                            <optgroup label={dtcUsers.length > 0 ? 'Other Users' : 'Users'}>
                              {otherUsers.map((user) => (
                                <option key={user.uid} value={user.uid}>{user.name}</option>
                              ))}
                            </optgroup>
                          </>
                        );
                      })()}
                    </ChakraSelect>
                  </FormControl>
                  {isViewingOtherUser && (
                    <Badge colorScheme="purple" fontSize="xs">Admin Mode</Badge>
                  )}
                </VStack>
              )}
              <Button
                data-tour="mm-new-newsletter"
                leftIcon={<FiPlus />}
                colorScheme="blue"
                onClick={() => handleCreateNewsletter()}
                isLoading={creating}
                loadingText="Creating..."
              >
                New Newsletter
              </Button>
            </HStack>
          </Flex>

          {isViewingOtherUser && (
            <Box mt={4} p={3} bg={bannerBg} borderRadius="md" borderWidth="1px" borderColor={bannerBorder}>
              <HStack spacing={2} align="flex-start">
                <Icon as={FiEye} color={bannerText} mt={0.5} />
                <Text fontSize="sm" color={bannerText}>
                  You are viewing marketing for{' '}
                  <Text as="span" fontWeight="bold">{viewedUser?.name || 'this user'}</Text>
                  {viewedUser?.email ? ` (${viewedUser.email})` : ''}. Anything you create or send
                  here belongs to them and emails go out from their Gmail.
                </Text>
              </HStack>
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Tabs
          isLazy
          colorScheme="blue"
          index={tabIndex}
          onChange={setTabIndex}
          variant="enclosed"
        >
          <TabList data-tour="mm-tabs">
            <Tab fontWeight="medium" data-tour="mm-tab-overview">Overview</Tab>
            <Tab fontWeight="medium" data-tour="mm-tab-newsletters">Newsletters</Tab>
            <Tab fontWeight="medium" data-tour="mm-tab-templates">Templates</Tab>
            <Tab fontWeight="medium" data-tour="mm-tab-activity">Activity</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <OverviewTab uid={effectiveUserId} onGoToNewsletters={() => setTabIndex(1)} />
            </TabPanel>
            <TabPanel px={0}>
              <NewslettersTab
                uid={effectiveUserId}
                newslettersHook={newslettersHook}
                onCreate={() => handleCreateNewsletter()}
              />
            </TabPanel>
            <TabPanel px={0}>
              <TemplatesTab onUseTemplate={handleCreateNewsletter} creating={creating} />
            </TabPanel>
            <TabPanel px={0}>
              <ActivityTab uid={effectiveUserId} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
    </Box>
  );
}
